import { Component, Input, Injector, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { RestrictionService } from 'src/app/controls/restrictions/services/restriction.service';
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { FormType } from 'src/app/common/enumerations/enumerations';
import { ProcessRestrictionResult } from '../../restrictions/services/restriction';
import { DocumentType } from 'src/app/services/documents/documents';

@Component({
  selector: 'cl-restrictions-by-form',
  templateUrl: './cl-restrictions-by-form.component.html',
  styleUrls: ['./cl-restrictions-by-form.component.scss']
})
export class ClRestrictionsByFormComponent extends ClBaseComponent implements OnChanges, OnDestroy {

  @Input() resourceID?: number;

  documents!: string;
  resource?: Resource;
  resources!: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  documentTypes: DocumentType[] = [];
  documentTypes$!: Observable<DocumentType[]>;
  documentTypesSubs!: Subscription;

  task?: WfTaskLocalResource;
  manual!: boolean;

  ppr?: ProcessRestrictionResult;

  constructor(
    protected override injector: Injector,
    private service: RestrictionService,
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    this.resourcesSubs?.unsubscribe();
    this.documentTypesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['valueString'] || changes['configuration'] || changes['disabled'] || changes['formType'] || changes['resourceID']) {
      this.loadResources();
      this.loadData();
    }
  }

  /**
   * Resolves the form selected for this restriction component.
   * The task configuration takes precedence over the parent checklist form type.
   */
  get effectiveFormType(): FormType | undefined {
    const configFormType = this.utils.JSONparse(this.configuration)?.formType;
    const formType = configFormType ?? this.formType;

    if (typeof formType === 'number')
      return formType as FormType;

    if (typeof formType === 'string') {
      const numericFormType = Number(formType);
      if (!Number.isNaN(numericFormType))
        return numericFormType as FormType;

      const enumFormType = FormType[formType as keyof typeof FormType];
      return typeof enumFormType === 'number' ? enumFormType : undefined;
    }

    return undefined;
  }

  get validateRS(): boolean {
    return !!this.utils.JSONparse(this.configuration)?.validateRS;
  }

  /**
   * Returns the configured form's display title from the Document Types catalog.
   */
  get formTypeName(): string {
    if (this.effectiveFormType == null) return '';
    const documentType = this.documentTypes.find(d => d.id === this.effectiveFormType);
    return documentType?.description ?? documentType?.name ?? FormType[this.effectiveFormType] ?? '';
  }

  get displayText(): string {
    return this.textBefore || `Review any related open <b class='blue'>${this.formTypeName}</b> to verify that key-enable may proceed.`;
  }

  loadResources() {
    this.resourcesSubs?.unsubscribe();
    this.resources$ = this.store.select(state => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe(data => {
      if (data.length) {
        this.resources = data;
      }
    });
    this.documentTypesSubs?.unsubscribe();
    this.documentTypes$ = this.store.select(state => state.DocumentType.data);
    this.documentTypesSubs = this.documentTypes$.subscribe(data => {
      this.documentTypes = data;
    });
  }

  loadData() {
    const config = this.utils.JSONparse(this.configuration);
    const locationID = config?.val ?? this.resourceID;
    this.resource = this.resources?.find(x => x.id == locationID);
    const wfSectionLocal = this.checklist?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.wfStepLocals?.some(t => t.wfTaskLocals.some(x => x.id === this.taskID)));
    const wfStepLocal = wfSectionLocal?.wfStepLocals?.find(s => s.wfTaskLocals.filter(x => x.id == this.taskID).length > 0);
    this.task = wfStepLocal?.wfTaskLocals?.find(x => x.id == this.taskID);
    const val = this.utils.JSONparse(this.valueString);
    this.checked = val?.user != null;
    this.manual = val?.manualCheck;
    this.documents = val?.documents;
    if (val?.user) {
      this.user = val.user === -1
        ? { id: val.user, name: 'ASCC', initials: 'ASCC' }
        : this.getUsers()?.find(u => u.id === val.user);
      if (!val.manualCheck)
        this.disabled = val.user === -1;
    }
    if (this.checked && !wfStepLocal?.wfStepStatusLocals?.filter(x => x.enabled || !x.hidden).map(x => x.status).includes(this.checklist?.status)) {
      this.displayInfo();
    }
    else {
      if (locationID && !val?.manualCheck) {
        this.getRestrictions();
      }
    }
  }

  async getRestrictions(): Promise<void> {
    const { val: configLocationID } = this.utils.JSONparse(this.configuration);
    const locationID = configLocationID ?? this.resourceID;

    if (!locationID) {
      this.displayInfo('');
      return;
    }

    const ppr = await firstValueFrom(
      this.service.CheckByLocationID(locationID)
    );

    if (!ppr) {
      this.displayInfo('');
      return;
    }

    const affectedForms = this.effectiveFormType != null
      ? (ppr.affectedForms ?? []).filter(f => f.formType === this.effectiveFormType)
      : ppr.affectedForms ?? [];
    const radSurveyAffectedForms = this.effectiveFormType != null
      ? (ppr.radSurveyAffectedForms ?? []).filter(f => f.formType === this.effectiveFormType)
      : ppr.radSurveyAffectedForms ?? [];

    this.ppr = { ...ppr, affectedForms, radSurveyAffectedForms };

    const formsToDisplay = [...affectedForms, ...radSurveyAffectedForms];

    this.displayInfo(
      formsToDisplay.length
        ? [...new Set(formsToDisplay.map(x => x.serialNo))].join(', ')
        : ''
    );

    if (this.editor || this.builder) {
      return;
    }

    const hasActiveRestrictions = affectedForms.length > 0;
    const hasRadSurveyRequirement = radSurveyAffectedForms.length > 0;
    const isReady = !hasActiveRestrictions && !hasRadSurveyRequirement;
    const wasChecked = !!this.checked;

    this.checked = isReady;
    this.disabled = isReady || hasActiveRestrictions;

    if (isReady) {
      this.setValues(-1);
    }
    else if (hasActiveRestrictions && wasChecked) {
      this.setValues();
    }
  }

  reset() {
    this.manual = false;
    this.checkboxChanged({ checked: false });
  }

  displayInfo(serialNumbers: string | null | undefined = null) {
    this.warning = !!serialNumbers;
    this.moreInfo = serialNumbers;
    this.documents = serialNumbers ?? '';
  }

  checkboxChanged(e: any) {
    this.checked = e.checked;
    if (this.checked && this.warning) {
      this.dialog.open(YesNoDialogComponent, {
        width: '500px',
        data: {
          message: this.getMessage('RestrictionsDocuments_Warning').description
            .replace('{location}', this.resource?.name ?? '')
            .replace('{documents}', this.moreInfo ?? ''),
          icon: 'stop',
          labelButtonCancel: 'Cancel',
          labelButtonAccept: 'Continue',
          changeButtonLabels: true
        },
      }).afterClosed().subscribe(data => {
        if (data) {
          this.setValues(this.getCurrentUser()?.id);
        }
        else {
          this.checked = false;
          this.loadData();
        }
      });
    }
    else if (this.checked) {
      this.setValues(this.getCurrentUser()?.id);
    }
    else {
      this.setValues();
    }
  }

  setValues(userID?: number | null) {
    const valueString = this.utils.JSONstringify({ documents: this.documents, user: userID, manualCheck: (userID && userID != -1) });
    if (this.valueString != valueString) {
      this.save.emit({ text: valueString, sign: { checked: this.checked, isASCC: userID == -1 } });
    }
  }
}
